import React, { useEffect, useState } from "react";
import { Dialog, Box, Typography, IconButton } from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ClockAlert } from 'lucide-react';
import CloseIcon from "@mui/icons-material/Close";

const SubscriptionExpiryDialog = () => {
  const [user, setUser] = useState(null);
  const [userSubscription, setUserSubscription] = useState(null);
  const [open, setOpen] = useState(false);
  const [messageType, setMessageType] = useState(null); // 'tomorrow' | 'expired' | null
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const response = await axios.get("https://admin.bizstor.lk/public/api/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          localStorage.removeItem("token");
          navigate("/");
        } else {
          console.error("Profile fetch error", error);
        }
      }
    };

    fetchProfile();
  }, [navigate]);

  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        const response = await axios.get("https://admin.bizstor.lk/public/api/get-subscription");
        const allSubs = response.data?.subscription || [];

        const matchedSub = allSubs
          .filter(sub => sub?.email === user.email && sub?.status === "active")
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

        setUserSubscription(matchedSub || null);
      } catch (error) {
        console.error("Failed to fetch subscriptions", error);
      }
    };

    if (user?.email) {
      fetchSubscriptions();
    }
  }, [user]);

  useEffect(() => {
    if (!userSubscription?.end_date) return;

    // Normalize to midnight to avoid time-of-day issues
    const endDate = new Date(userSubscription.end_date);
    const endMid = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

    const now = new Date();
    const todayMid = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const tomorrowMid = new Date(todayMid);
    tomorrowMid.setDate(tomorrowMid.getDate() + 1);

    // New rule: if end_date <= today -> expired
    const isExpired = endMid.getTime() <= todayMid.getTime();

    const isEndingTomorrow =
      endMid.getFullYear() === tomorrowMid.getFullYear() &&
      endMid.getMonth() === tomorrowMid.getMonth() &&
      endMid.getDate() === tomorrowMid.getDate();

    if (isExpired) {
      setMessageType("expired");
      setOpen(true);
    } else if (isEndingTomorrow) {
      setMessageType("tomorrow");
      setOpen(true);
    } else {
      setMessageType(null);
      setOpen(false);
    }
  }, [userSubscription]);

  const getTitle = () => {
    if (messageType === "tomorrow") return "Your subscription will expire tomorrow";
    if (messageType === "expired") return "Your package has expired";
    return "";
  };

  const getBody = () => {
    if (messageType === "tomorrow")
      return "Your subscription will expire tomorrow. Please renew your package to continue using the service.";
    if (messageType === "expired")
      return "Your subscription has already expired (end date is on or before today). Please renew your package to regain access.";
    return "";
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <Box sx={{ position: 'relative', px: 4, py: 3, minWidth: 300, textAlign: 'center' }}>
        <IconButton
          onClick={() => setOpen(false)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            '&:hover': { color: 'red' },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <ClockAlert size={40} color="#f59e0b" />
        </Box>

        <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
          {getTitle()}
        </Typography>

        <Typography sx={{ color: 'text.secondary' }}>
          {getBody()}
        </Typography>
      </Box>
    </Dialog>
  );
};

export default SubscriptionExpiryDialog;
